import React, { useState, useEffect } from 'react';
import { Database, UploadCloud, FileText, CheckCircle, Clock, Trash2 } from 'lucide-react';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../../config';
const BASE_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

export default function CommerceDataHub() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success' or null
  const [uploadHistory, setUploadHistory] = useState([]);

  
  const handleDeleteLog = async (id, filename) => {
    if (!window.confirm(`确定要删除 "${filename}" 的导入记录吗？\n(注意：这将从系统中清除该批次的底层罗盘订单数据)`)) return;
    
    try {
      const projectId = localStorage.getItem('globalBrand') || 1;
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.delete(`${BASE_URL}/compass/log/${filename}?projectId=${projectId}`, { headers });
      if (res.data.success) {
        setUploadHistory(prev => prev.filter(item => item.id !== id));
        window.dispatchEvent(new Event('globalFilterChange')); // Refresh analytics
      }
    } catch (err) {
      console.error(err);
      alert('删除失败');
    }
  };

  const fetchLogs = () => {
    const projectId = localStorage.getItem('globalBrand') || 1;
    const globalProject = localStorage.getItem('globalProject');
    let url = `${BASE_URL}/compass/logs?projectId=${projectId}`;
    if (globalProject && globalProject !== 'all' && globalProject !== 'undefined') {
        url += `&geoTaskId=${globalProject}`;
    }
    axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
        if (res.data && res.data.success) {
            setUploadHistory(res.data.data);
        }
    }).catch(err => console.error('Failed to fetch logs:', err));
  };

  useEffect(() => {
    fetchLogs();
    const handleFilterChange = () => fetchLogs();
    window.addEventListener('globalFilterChange', handleFilterChange);
    return () => window.removeEventListener('globalFilterChange', handleFilterChange);
  }, []);

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadStatus(null);
      const projectId = localStorage.getItem('globalBrand') || 1;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);

      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await axios.post(`${BASE_URL}/compass/import`, formData, { headers });
      if (res.data.success) {
        setUploadStatus('success');
        
        // Add to mock history log for UX
        const newLog = {
            id: Date.now(),
            date: new Date().toLocaleString('zh-CN', { hour12: false }),
            filename: file.name,
            records: res.data.message.match(/\d+/g)?.[0] || 'Unknown',
            status: '归因完成 (已入库)'
        };
        setUploadHistory(prev => [newLog, ...prev]);
        
        // trigger event to update other pages if they are mounted
        window.dispatchEvent(new Event('globalFilterChange'));
      }
    } catch (err) {
      alert("导入失败: " + err.message);
    } finally {
      setIsUploading(false);
      e.target.value = null; // reset
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Database className="text-blue-500 w-6 h-6" />罗盘数据中枢 (Data Hub)
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            将业务后端的订单明细（Raw Data）导入系统，作为“转化归因模型”的基础数据源。顶部已选中品牌，导入的数据将严格归属当前品牌。
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Area */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-dashed border-gray-300 dark:border-slate-600 p-10 flex flex-col items-center justify-center text-center">
          <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <UploadCloud className="w-10 h-10 text-blue-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">上传罗盘/生意参谋订单明细</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 max-w-md mb-6">
            请导出包含“支付完成时间”、“实付金额”、“商品ID”及“APP渠道 (如豆包)”等维度字段的订单报表（支持 CSV / Excel）。
          </p>
          
          <input type="file" id="import-compass-hub" className="hidden" accept=".xlsx,.xls,.csv" onChange={handleImport} />
          <label htmlFor="import-compass-hub" className={`cursor-pointer flex items-center gap-2 ${isUploading ? 'bg-gray-400' : uploadStatus === 'success' ? 'bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700'} text-white px-8 py-3 rounded-lg font-medium transition-all`}>
            {isUploading ? (
              <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />解析并撞库中...</>
            ) : uploadStatus === 'success' ? (
              <><CheckCircle className="w-5 h-5" />上传成功，查看下方流水</>
            ) : (
              <><UploadCloud className="w-5 h-5" />选择文件上传</>
            )}
          </label>
        </div>

        {/* API Lock Card */}
        <div className="bg-slate-50 dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700 p-6 flex flex-col justify-center items-center text-center relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-3"><span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded font-bold">研发中</span></div>
          <div className="w-16 h-16 bg-slate-200 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4 text-slate-400">
            <Database className="w-8 h-8" />
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">API 官方直连授权</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
            授权抖音电商开放平台 API，实现分钟级自动拉取订单及渠道流向数据，彻底告别手工导表。
          </p>
          <button className="w-full py-2 bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 rounded-lg cursor-not-allowed font-medium text-sm">
            功能锁定期
          </button>
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 shadow-sm p-5">
        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />数据导入流水账
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-slate-700 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">导入时间</th>
                <th className="px-4 py-3">源文件</th>
                <th className="px-4 py-3 text-right">解析成功记录数</th>
                <th className="px-4 py-3">归因状态</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {uploadHistory.map((item) => (
                <tr key={item.id} className="border-b dark:border-slate-700">
                  <td className="px-4 py-3">{item.date}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-500"/>{item.filename}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-600 font-bold">{item.records.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-1 rounded dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center w-fit gap-1">
                      <CheckCircle className="w-3 h-3"/>{item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDeleteLog(item.id, item.filename)} className="text-gray-400 hover:text-red-500 transition-colors p-1" title="删除此批次数据"><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
